import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    UserCheck,
    Mail,
    Phone,
    Calendar,
    Edit,
    Plus,
    Crown,
    Shield,
    Briefcase,
    Users,
    CheckCircle,
    Clock
} from 'lucide-react';

const projectRoles = [
    {
        role: 'Chef de Projet',
        description: 'Leadership et coordination du projet',
        assignee: {
            name: 'Marie Rakoto',
            email: 'm.rakoto@basan.mg',
            phone: '+261 34 12 345 67',
            department: 'R&D Produits',
            availability: 'available'
        },
        responsibilities: ['Coordination générale', 'Suivi planning', 'Communication stakeholders'],
        level: 'primary'
    },
    {
        role: 'Direction Technique',
        description: 'Validation technique et supervision',
        assignee: {
            name: 'Paul Andriamahefa',
            email: 'p.andriamahefa@basan.mg',
            phone: '+261 34 23 456 78',
            department: 'Direction Technique',
            availability: 'busy'
        },
        responsibilities: ['Validation spécifications', 'Supervision technique', 'Contrôle qualité'],
        level: 'executive'
    },
    {
        role: 'Direction Financière',
        description: 'Contrôle budgétaire et approbations',
        assignee: {
            name: 'Sophie Ranaivo',
            email: 's.ranaivo@basan.mg',
            phone: '+261 34 34 567 89',
            department: 'Direction Financière',
            availability: 'available'
        },
        responsibilities: ['Validation budget', 'Contrôle dépenses', 'Reporting financier'],
        level: 'executive'
    },
    {
        role: 'Validation Métier',
        description: 'Validation business case et ROI',
        assignee: {
            name: 'Jean Rakotoarison',
            email: 'j.rakotoarison@basan.mg',
            phone: '+261 34 45 678 90',
            department: 'Commercial & Marketing',
            availability: 'scheduled'
        },
        responsibilities: ['Validation ROI', 'Impact business', 'Acceptation utilisateurs'],
        level: 'functional'
    },
    {
        role: 'Référent CAPEX',
        description: 'Gouvernance processus et méthodologie',
        assignee: {
            name: 'Hery Rasolofo',
            email: 'h.rasolofo@basan.mg',
            phone: '+261 34 56 789 01',
            department: 'Direction Générale',
            availability: 'available'
        },
        responsibilities: ['Conformité processus', 'Méthodologie CAPEX', 'Reporting direction'],
        level: 'governance'
    }
];

const raciMatrix = [
    {
        activity: 'Définition cahier des charges',
        chefProjet: 'R',
        directionTechnique: 'A',
        directionFinanciere: 'I',
        validationMetier: 'C',
        referentCapex: 'I'
    },
    {
        activity: 'Validation budget initial',
        chefProjet: 'R',
        directionTechnique: 'C',
        directionFinanciere: 'A',
        validationMetier: 'C',
        referentCapex: 'I'
    },
    {
        activity: 'Sélection fournisseurs',
        chefProjet: 'R',
        directionTechnique: 'C',
        directionFinanciere: 'C',
        validationMetier: 'I',
        referentCapex: 'I'
    },
    {
        activity: 'Validation finale projet',
        chefProjet: 'R',
        directionTechnique: 'A',
        directionFinanciere: 'A',
        validationMetier: 'A',
        referentCapex: 'C'
    },
    {
        activity: 'Mise en production',
        chefProjet: 'R',
        directionTechnique: 'A',
        directionFinanciere: 'I',
        validationMetier: 'A',
        referentCapex: 'I'
    }
];

const levelConfig = {
    primary: { icon: Crown, color: 'bg-yellow-100 text-yellow-800', bgColor: 'from-yellow-50 to-yellow-100' },
    executive: { icon: Shield, color: 'bg-purple-100 text-purple-800', bgColor: 'from-purple-50 to-purple-100' },
    functional: { icon: Briefcase, color: 'bg-blue-100 text-blue-800', bgColor: 'from-blue-50 to-blue-100' },
    governance: { icon: Users, color: 'bg-teal-100 text-teal-800', bgColor: 'from-teal-50 to-teal-100' }
};

const availabilityConfig = {
    available: { label: 'Disponible', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    busy: { label: 'Occupé', color: 'bg-red-100 text-red-800', icon: Clock },
    scheduled: { label: 'Planifié', color: 'bg-yellow-100 text-yellow-800', icon: Calendar }
};

const raciColors = {
    R: 'bg-red-100 text-red-800',
    A: 'bg-green-100 text-green-800',
    C: 'bg-blue-100 text-blue-800',
    I: 'bg-gray-100 text-gray-800'
};

const raciLabels = {
    R: 'Responsable',
    A: 'Approbateur',
    C: 'Consulté',
    I: 'Informé'
};

// @ts-nocheck
export default function ResponsablesTab({ project }) {
    const [selectedRole, setSelectedRole] = useState(null);

    return (
        <div className="space-y-8">
            {/* Team Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-teal-600 mb-1">Équipe projet</p>
                                <p className="text-3xl font-bold text-teal-900">{projectRoles.length}</p>
                                <p className="text-sm text-teal-600 mt-1">rôles définis</p>
                            </div>
                            <div className="w-12 h-12 bg-teal-200 rounded-2xl flex items-center justify-center">
                                <Users className="w-6 h-6 text-teal-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600 mb-1">Disponibles</p>
                                <p className="text-3xl font-bold text-green-900">
                                    {projectRoles.filter(r => r.assignee.availability === 'available').length}
                                </p>
                                <p className="text-sm text-green-600 mt-1">membres actifs</p>
                            </div>
                            <div className="w-12 h-12 bg-green-200 rounded-2xl flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-purple-600 mb-1">Directions</p>
                                <p className="text-3xl font-bold text-purple-900">
                                    {projectRoles.filter(r => r.level === 'executive').length}
                                </p>
                                <p className="text-sm text-purple-600 mt-1">niveaux exec.</p>
                            </div>
                            <div className="w-12 h-12 bg-purple-200 rounded-2xl flex items-center justify-center">
                                <Shield className="w-6 h-6 text-purple-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600 mb-1">Responsabilités</p>
                                <p className="text-3xl font-bold text-blue-900">15</p>
                                <p className="text-sm text-blue-600 mt-1">total définies</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-200 rounded-2xl flex items-center justify-center">
                                <UserCheck className="w-6 h-6 text-blue-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Team Structure */}
            <Card className="border-0 shadow-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-xl font-bold text-gray-900">Structure de l'équipe projet</CardTitle>
                    <Button className="bg-teal-600 hover:bg-teal-700 gap-2">
                        <Plus className="w-4 h-4" />
                        Assigner rôle
                    </Button>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {projectRoles.map((roleData, index) => {
                            const LevelIcon = levelConfig[roleData.level]?.icon || Users;
                            const availability = availabilityConfig[roleData.assignee.availability];

                            return (
                                <Card
                                    key={index}
                                    className={`border-0 shadow-md bg-gradient-to-br ${levelConfig[roleData.level]?.bgColor || 'from-gray-50 to-gray-100'} hover:shadow-lg transition-all duration-200 cursor-pointer`}
                                    onClick={() => setSelectedRole(roleData)}
                                >
                                    <CardContent className="p-6">
                                        <div className="flex items-start gap-4">
                                            <div className="relative">
                                                <Avatar className="w-12 h-12">
                                                    <AvatarFallback className="bg-white text-gray-700 font-semibold">
                                                        {roleData.assignee.name.split(' ').map(n => n[0]).join('')}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="absolute -bottom-1 -right-1">
                                                    <div className={`w-6 h-6 rounded-full ${levelConfig[roleData.level]?.color || 'bg-gray-100 text-gray-800'} flex items-center justify-center`}>
                                                        <LevelIcon className="w-3 h-3" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="font-bold text-gray-900">{roleData.role}</h3>
                                                    <Badge className={availability?.color}>
                                                        <availability.icon className="w-3 h-3 mr-1" />
                                                        {availability?.label}
                                                    </Badge>
                                                </div>

                                                <p className="text-sm text-gray-600 mb-2">{roleData.description}</p>

                                                <div className="mb-3">
                                                    <p className="font-medium text-gray-900">{roleData.assignee.name}</p>
                                                    <p className="text-sm text-gray-600">{roleData.assignee.department}</p>
                                                </div>

                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <div className="flex items-center gap-1">
                                                        <Mail className="w-3 h-3" />
                                                        {roleData.assignee.email}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Phone className="w-3 h-3" />
                                                        {roleData.assignee.phone}
                                                    </div>
                                                </div>

                                                <div className="mt-3">
                                                    <p className="text-xs font-medium text-gray-700 mb-1">Responsabilités:</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {roleData.responsibilities.slice(0, 2).map((resp, idx) => (
                                                            <Badge key={idx} variant="outline" className="text-xs">
                                                                {resp}
                                                            </Badge>
                                                        ))}
                                                        {roleData.responsibilities.length > 2 && (
                                                            <Badge variant="outline" className="text-xs">
                                                                +{roleData.responsibilities.length - 2}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* RACI Matrix */}
            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl font-bold text-gray-900">Matrice de responsabilités (RACI)</CardTitle>
                    <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-1">
                            <Badge className={raciColors.R}>R</Badge>
                            <span>Responsable</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Badge className={raciColors.A}>A</Badge>
                            <span>Approbateur</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Badge className={raciColors.C}>C</Badge>
                            <span>Consulté</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Badge className={raciColors.I}>I</Badge>
                            <span>Informé</span>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-64">Activité</TableHead>
                                    <TableHead className="text-center">Chef Projet</TableHead>
                                    <TableHead className="text-center">Dir. Technique</TableHead>
                                    <TableHead className="text-center">Dir. Financière</TableHead>
                                    <TableHead className="text-center">Valid. Métier</TableHead>
                                    <TableHead className="text-center">Réf. CAPEX</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {raciMatrix.map((activity, index) => (
                                    <TableRow key={index} className="hover:bg-gray-50">
                                        <TableCell className="font-medium">{activity.activity}</TableCell>
                                        <TableCell className="text-center">
                                            <Badge className={raciColors[activity.chefProjet]} title={raciLabels[activity.chefProjet]}>
                                                {activity.chefProjet}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge className={raciColors[activity.directionTechnique]} title={raciLabels[activity.directionTechnique]}>
                                                {activity.directionTechnique}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge className={raciColors[activity.directionFinanciere]} title={raciLabels[activity.directionFinanciere]}>
                                                {activity.directionFinanciere}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge className={raciColors[activity.validationMetier]} title={raciLabels[activity.validationMetier]}>
                                                {activity.validationMetier}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge className={raciColors[activity.referentCapex]} title={raciLabels[activity.referentCapex]}>
                                                {activity.referentCapex}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
